import './App.css';
import generalWords from './general.json';
import a2Words from './a2.json';
import a1Words from './a1.json';
import { useEffect, useState, useMemo } from 'react';

const STORAGE_KEY = 'currentWordIndex';
const DIFFICULTY_KEY = 'selectedDifficulties';
const STARRED_KEY = 'starred';
const SOURCE_KEY = 'wordSource';

const SOURCES = [
  { id: 'a1', label: 'A1' },
  { id: 'a2', label: 'A2' },
  { id: 'general', label: 'Genel' },
];

const DIFFICULTIES = [
  { key: 'kolay', label: 'Kolay' },
  { key: 'orta', label: 'Orta' },
  { key: 'zor', label: 'Zor' },
  { key: 'cok_zor', label: 'Çok Zor' },
];

function App() {
  const [source, setSource] = useState(() => {
    try {
      const stored = window.localStorage.getItem(SOURCE_KEY);
      if (stored === 'a1' || stored === 'a2' || stored === 'general') return stored;
    } catch {}
    return 'a1';
  });

  const words = source === 'a1' ? a1Words : source === 'a2' ? a2Words : generalWords;

  const difficultyCounts = useMemo(() => {
    return words.reduce((acc, w) => {
      acc[w.difficulty] = (acc[w.difficulty] || 0) + 1;
      return acc;
    }, {});
  }, [words]);
  const [selectedDifficulties, setSelectedDifficulties] = useState(() => {
    try {
      const stored = window.localStorage.getItem(DIFFICULTY_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return ['kolay', 'orta', 'zor', 'cok_zor'];
  });

  const [starred, setStarred] = useState(() => {
    try {
      const stored = window.localStorage.getItem(STARRED_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {}
    return [];
  });

  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [jumpModalOpen, setJumpModalOpen] = useState(false);
  const [jumpInput, setJumpInput] = useState('');
  const [jumpError, setJumpError] = useState('');
  const [revealed, setRevealed] = useState(false);

  const filteredWords = useMemo(() => {
    if (showStarredOnly) {
      return words.filter((w) => starred.includes(w.wordNl));
    }
    return words.filter((w) => selectedDifficulties.includes(w.difficulty));
  }, [words, selectedDifficulties, showStarredOnly, starred]);

  const indexKey = `${STORAGE_KEY}_${source}`;

  const [index, setIndex] = useState(() => {
    try {
      const stored = window.localStorage.getItem(indexKey);
      const n = stored != null ? parseInt(stored, 10) : 0;
      if (!Number.isNaN(n) && n >= 0) return n;
    } catch {}
    return 0;
  });

  const safeIndex = Math.min(index, Math.max(0, filteredWords.length - 1));
  const currentWord = filteredWords[safeIndex] || words[0];
  const totalWords = filteredWords.length;

  const canGoBack = safeIndex > 0;
  const canGoNext = safeIndex < totalWords - 1;

  const isCurrentStarred = currentWord ? starred.includes(currentWord.wordNl) : false;

  const handleNext = () => {
    setIndex((prev) => Math.min(prev + 1, totalWords - 1));
  };

  const handleBack = () => {
    setIndex((prev) => Math.max(prev - 1, 0));
  };

  const toggleStar = () => {
    if (!currentWord) return;
    setStarred((prev) => {
      if (prev.includes(currentWord.wordNl)) {
        return prev.filter((w) => w !== currentWord.wordNl);
      }
      return [...prev, currentWord.wordNl];
    });
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(SOURCE_KEY, source);
    } catch {}
  }, [source]);

  useEffect(() => {
    try {
      window.localStorage.setItem(indexKey, String(index));
    } catch {}
  }, [index, indexKey]);

  const handleSourceChange = (e) => {
    const newSource = e.target.value;
    if (newSource === 'a1' || newSource === 'a2' || newSource === 'general') {
      setSource(newSource);
      try {
        const stored = window.localStorage.getItem(`${STORAGE_KEY}_${newSource}`);
        const n = stored != null ? parseInt(stored, 10) : 0;
        setIndex(Number.isNaN(n) || n < 0 ? 0 : n);
      } catch {
        setIndex(0);
      }
    }
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(DIFFICULTY_KEY, JSON.stringify(selectedDifficulties));
    } catch {}
  }, [selectedDifficulties]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STARRED_KEY, JSON.stringify(starred));
    } catch {}
  }, [starred]);

  useEffect(() => {
    if (index >= filteredWords.length) {
      setIndex(Math.max(0, filteredWords.length - 1));
    }
  }, [filteredWords.length, index]);

  useEffect(() => {
    setRevealed(false);
  }, [safeIndex, source]);

  const toggleDifficulty = (key) => {
    if (showStarredOnly) return;
    setSelectedDifficulties((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) return prev;
        return prev.filter((d) => d !== key);
      }
      return [...prev, key];
    });
  };

  const toggleStarredOnly = () => {
    if (!showStarredOnly && starred.length === 0) {
      return;
    }
    setShowStarredOnly((prev) => !prev);
    setIndex(0);
  };

  const openTranslate = (text) => {
    const url = `https://translate.google.com/?sl=nl&tl=tr&text=${encodeURIComponent(text)}&op=translate`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleJumpOpen = () => {
    setJumpInput('');
    setJumpError('');
    setJumpModalOpen(true);
  };

  const handleJumpSubmit = () => {
    const num = parseInt(jumpInput, 10);
    if (Number.isNaN(num) || num < 1) {
      setJumpError('Geçerli bir numara girin.');
      return;
    }
    if (num > totalWords) {
      setJumpError(`Maksimum ${totalWords} kelime var.`);
      return;
    }
    setIndex(num - 1);
    setJumpModalOpen(false);
  };

  const handleJumpKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleJumpSubmit();
    } else if (e.key === 'Escape') {
      setJumpModalOpen(false);
    }
  };

  const difficultyLabel = DIFFICULTIES.find((d) => d.key === currentWord?.difficulty)?.label || '';

  const sentenceNl = currentWord?.sentenceNl || currentWord?.verbDetails?.ik || '';
  const sentenceTr = currentWord?.sentenceTr || '';
  const conjugations = currentWord?.verbConjugations || null;

  return (
    <div className="app" onClick={() => { dropdownOpen && setDropdownOpen(false); }}>
      <div className="container">
        <div className="meta">
          <select
            className="source-select"
            value={source}
            onChange={handleSourceChange}
            aria-label="Kelime listesi"
          >
            {SOURCES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
          <button type="button" className="counter-btn" onClick={handleJumpOpen}>
            {safeIndex + 1} / {totalWords}
          </button>
          
          <div className="filter-wrapper" onClick={(e) => e.stopPropagation()}>
            <button 
              type="button" 
              className="filter-btn"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {showStarredOnly ? '⭐ Yıldızlılar' : 'Seviye'} <span className="arrow">▼</span>
            </button>
            {dropdownOpen && (
              <div className="dropdown">
                <label className="dropdown-item starred-item">
                  <input
                    type="checkbox"
                    checked={showStarredOnly}
                    onChange={toggleStarredOnly}
                    disabled={starred.length === 0 && !showStarredOnly}
                  />
                  <span>⭐ Yıldızlılar ({starred.length})</span>
                </label>
                <div className="dropdown-divider" />
                {DIFFICULTIES.map((d) => (
                  <label key={d.key} className={`dropdown-item ${showStarredOnly ? 'disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={!showStarredOnly && selectedDifficulties.includes(d.key)}
                      onChange={() => toggleDifficulty(d.key)}
                      disabled={showStarredOnly}
                    />
                    <span>{d.label} ({difficultyCounts[d.key] || 0})</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="type-row">
          <span className="type-badge">{currentWord?.type === 'verb' ? 'Fiil' : 'Kelime'}</span>
          <span className="difficulty-badge" data-level={currentWord?.difficulty}>{difficultyLabel}</span>
          <button 
            type="button" 
            className={`star-btn ${isCurrentStarred ? 'starred' : ''}`}
            onClick={toggleStar}
            aria-label={isCurrentStarred ? 'Yıldızı kaldır' : 'Yıldız ekle'}
          >
            {isCurrentStarred ? '★' : '☆'}
          </button>
        </div>

        <div className="word-row">
          <h1 className="word-nl" onClick={() => openTranslate(currentWord?.wordNl)} style={{cursor: 'pointer'}}>
            {currentWord?.wordNl}
          </h1>
        </div>

        {!revealed ? (
          <button type="button" className="reveal-btn" onClick={() => setRevealed(true)}>
            <span className="reveal-text">Çeviriyi Gör</span>
          </button>
        ) : (
          <div className="reveal-container">
            <div className="word-translations">
              <span>{currentWord?.wordTr}</span>
              {currentWord?.wordEn && <span className="divider">•</span>}
              {currentWord?.wordEn && <span className="en">{currentWord?.wordEn}</span>}
            </div>

            {sentenceNl && (
              <button type="button" className="sentence-nl" onClick={() => openTranslate(sentenceNl)}>
                {sentenceNl}
              </button>
            )}

            {sentenceTr && (
              <p className="sentence-tr">{sentenceTr}</p>
            )}

            {conjugations && conjugations.length > 0 && (
              <div className="conjugations">
                {conjugations.map((c, i) => (
                  <span key={i} className="conj-item">{c}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <nav className="nav">
        <button type="button" onClick={handleBack} disabled={!canGoBack}>← Geri</button>
        <button type="button" onClick={handleNext} disabled={!canGoNext}>İleri →</button>
      </nav>

      {jumpModalOpen && (
        <div className="modal-overlay" onClick={() => setJumpModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Kelimeye Git</div>
            <div className="modal-subtitle">1 ile {totalWords} arasında bir numara girin</div>
            <input
              type="number"
              className="modal-input"
              value={jumpInput}
              onChange={(e) => { setJumpInput(e.target.value); setJumpError(''); }}
              onKeyDown={handleJumpKeyDown}
              placeholder="Numara"
              autoFocus
              min="1"
              max={totalWords}
            />
            {jumpError && <div className="modal-error">{jumpError}</div>}
            <div className="modal-actions">
              <button type="button" className="modal-cancel" onClick={() => setJumpModalOpen(false)}>İptal</button>
              <button type="button" className="modal-submit" onClick={handleJumpSubmit}>Git</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
